import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

// Extend window type for dataLayer
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

type ConsentStatus = 'pending' | 'accepted' | 'rejected';

const CONSENT_KEY = 'cookie_consent';
const GA_ID = 'G-NSP182M52B';

// Function to load Google Analytics
const loadGoogleAnalytics = () => {
  if (typeof window === 'undefined') return;
  
  // Check if already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_ID}"]`)) {
    return;
  }

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  gtag('js', new Date());
  gtag('config', GA_ID);
  
  // Make gtag available globally
  window.gtag = gtag;
};

// Function to remove Google Analytics cookies
const removeGoogleAnalyticsCookies = () => {
  const cookies = document.cookie.split(';');
  const gaCookies = cookies.filter(cookie => 
    cookie.trim().startsWith('_ga') || 
    cookie.trim().startsWith('_gid') ||
    cookie.trim().startsWith('_gat')
  );
  
  gaCookies.forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
};

const CookieConsentBanner = () => {
  const [consent, setConsent] = useState<ConsentStatus>('pending');
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const savedConsent = localStorage.getItem(CONSENT_KEY) as ConsentStatus | null;
    
    if (savedConsent === 'accepted') {
      setConsent('accepted');
      loadGoogleAnalytics();
    } else if (savedConsent === 'rejected') {
      setConsent('rejected');
    } else {
      // No consent yet, show banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setConsent('accepted');
    setShowBanner(false);
    loadGoogleAnalytics();
  };

  const handleRejectAll = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected');
    setConsent('rejected');
    setShowBanner(false);
    removeGoogleAnalyticsCookies();
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected');
    setConsent('rejected');
    setShowBanner(false);
    removeGoogleAnalyticsCookies();
  };

  if (!showBanner || consent !== 'pending') {
    return null;
  }

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-in slide-in-from-bottom-5 duration-300"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="container mx-auto max-w-4xl">
        <div className="bg-card border border-border rounded-xl shadow-xl p-6">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex shrink-0 w-12 h-12 rounded-full bg-primary/10 items-center justify-center">
              <Cookie className="h-6 w-6 text-primary" />
            </div>
            
            <div className="flex-1 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    🍪 Utilizziamo i cookie
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Utilizziamo cookie tecnici necessari e, con il tuo consenso, cookie analitici per migliorare la tua esperienza di navigazione.
                  </p>
                </div>
                <button
                  onClick={handleRejectAll}
                  className="shrink-0 p-1 hover:bg-muted rounded-md transition-colors sm:hidden"
                  aria-label="Chiudi"
                >
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>

              {showDetails && (
                <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-sm">
                  <div>
                    <h3 className="font-medium text-foreground">Cookie Tecnici (sempre attivi)</h3>
                    <p className="text-muted-foreground">
                      Necessari per il funzionamento del sito: sessione, carrello, preferenze.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">Cookie Analitici (Google Analytics)</h3>
                    <p className="text-muted-foreground">
                      Ci aiutano a capire come utilizzi il sito per migliorarlo. I dati sono anonimi e aggregati.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex gap-2 flex-1">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 sm:flex-none"
                  >
                    Accetta tutti
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleAcceptNecessary}
                    className="flex-1 sm:flex-none"
                  >
                    Solo necessari
                  </Button>
                </div>
                
                <div className="flex items-center gap-3 text-sm">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
                  >
                    {showDetails ? 'Nascondi dettagli' : 'Dettagli'}
                  </button>
                  <Link
                    to="/cookie-policy"
                    className="text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
                  >
                    Cookie Policy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export a function to check consent status
export const hasAnalyticsConsent = (): boolean => {
  return localStorage.getItem(CONSENT_KEY) === 'accepted';
};

// Export a function to reset consent (for Cookie Policy page)
export const resetCookieConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
  removeGoogleAnalyticsCookies();
  window.location.reload();
};

export default CookieConsentBanner;
